import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

const DexControlsContext = createContext(null);

function fallbackPress(name) {
  const delta = name === 'onUp' ? -180 : name === 'onDown' ? 180 : 0;
  if (delta) window.scrollBy({ top: delta, behavior: 'smooth' });
}

function filterRoot(node) {
  return node?.closest?.('[data-filter-root]') || null;
}

function moveFilterFocus(direction) {
  const active = document.activeElement;
  const root = filterRoot(active);
  if (!root) return false;
  const tag = active.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  const nodes = [...root.querySelectorAll('button, a')].filter((el) => !el.disabled);
  if (!nodes.includes(active)) return false;
  const rect = active.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  let best = null;
  let bestScore = Infinity;
  nodes.forEach((el) => {
    if (el === active) return;
    const box = el.getBoundingClientRect();
    const dx = (box.left + box.width / 2) - cx;
    const dy = (box.top + box.height / 2) - cy;
    const vertical = direction === 'up' || direction === 'down';
    const sign = direction === 'down' || direction === 'right' ? 1 : -1;
    if (vertical ? Math.sign(dy) !== sign : Math.sign(dx) !== sign) return;
    const primary = vertical ? Math.abs(dy) : Math.abs(dx);
    const secondary = vertical ? Math.abs(dx) : Math.abs(dy);
    if (primary < 2) return;
    const score = primary + secondary * 3;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  });
  if (best) best.focus();
  return true;
}

const DIRECTION = {
  onUp: 'up',
  onDown: 'down',
  onLeft: 'left',
  onRight: 'right',
};

export function DexControlsProvider({ children }) {
  const stackRef = useRef([]);
  const runTopRef = useRef(() => false);
  runTopRef.current = (name) => {
    const stack = stackRef.current;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      const handler = stack[i].current?.[name];
      if (typeof handler !== 'function') continue;
      if (handler() !== false) return true;
    }
    return false;
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const root = filterRoot(event.target);
      if (root && (tag === 'BUTTON' || tag === 'A')) {
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const direction = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' }[event.key];
          moveFilterFocus(direction);
          return;
        }
        if (event.key === 'a' || event.key === 'A') {
          event.preventDefault();
          event.target.click();
          return;
        }
        if (event.key === 'Enter') return;
      }
      const keyMap = {
        ArrowUp: 'onUp',
        ArrowDown: 'onDown',
        ArrowLeft: 'onLeft',
        ArrowRight: 'onRight',
        a: 'onA',
        A: 'onA',
        b: 'onB',
        B: 'onB',
        Enter: 'onA',
      };
      const name = keyMap[event.key];
      if (!name) return;
      if (!runTopRef.current(name)) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const press = useCallback((name) => {
    const active = document.activeElement;
    const root = filterRoot(active);
    const tag = active?.tagName;
    if (root && (tag === 'BUTTON' || tag === 'A')) {
      if (name === 'onA') {
        active.click();
        return;
      }
      if (DIRECTION[name]) {
        moveFilterFocus(DIRECTION[name]);
        return;
      }
    }
    if (!runTopRef.current(name)) fallbackPress(name);
  }, []);

  const register = useCallback((ref) => {
    stackRef.current = [...stackRef.current, ref];
    return () => {
      stackRef.current = stackRef.current.filter((item) => item !== ref);
    };
  }, []);

  const value = useMemo(() => ({ press, register }), [press, register]);

  return (
    <DexControlsContext.Provider value={value}>
      {children}
    </DexControlsContext.Provider>
  );
}

export function useDexControls(handlers, enabled = true) {
  const context = useContext(DexControlsContext);
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!context || !enabled) return undefined;
    return context.register(ref);
  }, [context, enabled]);
}

export function useDexPress() {
  const context = useContext(DexControlsContext);
  return context?.press || (() => {});
}
